import styles from 'css/src/components/common/comments/VideoComment.module.scss';

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { PlainTextPlugin } from '@lexical/react/LexicalPlainTextPlugin';

import { formatDistanceToNow, isAfter, parseISO } from 'date-fns';
import { CreationSourceEnum } from 'js/src/client';
import { MentionNode } from 'js/src/components/common/comments/MentionNode';
import Avatar from 'js/src/components/common/ui/Avatar';
import { BUTTON_SIZES, BUTTON_VARIANTS, TertiaryButton } from 'js/src/components/common/ui/Button';
import Tooltip, { Placement } from 'js/src/components/common/ui/Tooltip';
import {
    deleteComment,
    getLastUpdatedByCommentId,
    getMessageByCommentId,
    getUserByCommentId,
    getUserDisplayEmailByCommentId,
    getUserDisplayNameByCommentId,
    getUserPhotoUrlByCommentId,
} from 'js/src/data/comments';
import { getUserInitials } from 'js/src/libs/custom-utils';
import { ErrorReporter } from 'js/src/libs/error-reporter';
import { useAppDispatch } from 'js/src/libs/RootState';

const CONTENT_EDITABLE = <ContentEditable />;
const NODES = [MentionNode];
const NO_COMMENT_IDS: readonly number[] = [];

const nameInterpolation = (first_name = '', last_name = '', email: string | null) => {
    let userName = '';
    if (first_name !== '') {
        if (last_name !== '') {
            userName = `${first_name} ${last_name}`;
        } else {
            userName = first_name;
        }
    } else if (email) {
        userName = email;
    }

    return userName;
};

type VideoCommentProps = {
    commentId: number;
    threadType?: string;
    threadAction?: (data?: { comment_id: number }) => void;
};

/**
 * This component renders comments in the creator.
 * Comments currently displays
 * <Avatar /> : user image or initials
 * first name of the user's name,
 * time of the comment,
 * delete button,
 * message of the comment in lexical form
 */
export const VideoComment = React.memo(function VideoComment({
    commentId,
    threadType,
    threadAction,
}: VideoCommentProps) {
    const dispatch = useAppDispatch();
    const message = useSelector(getMessageByCommentId(commentId));
    const [editorState, setEditorState] = React.useState<string | null>(null);

    // user info
    const user = useSelector(getUserByCommentId(commentId));
    const first_name = user?.first_name;
    const last_name = user?.last_name;
    const userEmail = user?.email;
    const user_id = user?.id;
    const externalUserName = useSelector(getUserDisplayNameByCommentId(commentId));
    const externalEmail = useSelector(getUserDisplayEmailByCommentId(commentId));
    const email = userEmail || externalEmail;
    const photoUrl = useSelector(getUserPhotoUrlByCommentId(commentId));
    const last_updated = useSelector(getLastUpdatedByCommentId(commentId));
    const isCommentByCurrentUser = window.SERVER.USER && user_id === window.SERVER.USER.id;

    useEffect(() => {
        const editorState = JSON.stringify(message);
        setEditorState(editorState);
    }, [message]);

    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch(
            deleteComment({
                comment_id: commentId,
            }),
        );
    };

    const handleResolveComment = React.useCallback(() => {
        analytics.track('Resolved Comment Thread', {
            user_id: window.SERVER.USER.id,
        });
        if (!threadAction) throw new Error('threadAction is required to resolve comment');
        threadAction();
    }, [threadAction]);

    const handleReopenComment = React.useCallback(() => {
        if (!threadAction) throw new Error('threadAction is required to reopen comment');
        threadAction({
            comment_id: commentId,
        });
    }, [commentId, threadAction]);

    const userName =
        first_name || last_name
            ? nameInterpolation(first_name, last_name, email)
            : externalUserName;
    const userInitials = getUserInitials(userName, email);
    // replaces default date-fns text with a shorter text
    const lastUpdatedDate =
        typeof last_updated === 'string' ? parseISO(last_updated) : new Date(last_updated);
    // sometimes server/client clocks aren't in sync which results in displaying "less than a minute ago", isAfter resolves this
    const commentTime = isAfter(lastUpdatedDate, new Date())
        ? formatDistanceToNow(new Date(), { addSuffix: true })
        : formatDistanceToNow(lastUpdatedDate, { addSuffix: true }).replace(
              'less than a minute ago',
              'Just now',
          );

    const onLexicalError = React.useCallback((e: Error) => {
        ErrorReporter.capture(e);
    }, []);

    if (!editorState) {
        return null;
    }

    return (
        <div
            className={classNames(styles['video-comment'], 'px-l5-8', {
                'resolved-video-comment': threadType === 'RESOLVED',
            })}
            data-testid="video-comment"
        >
            <div className={styles['video-comment__header']}>
                <div className={styles['video-comment__avatar']}>
                    <Avatar
                        image={photoUrl}
                        text={userInitials}
                        textCustomClass="border-space"
                        size="small"
                    />
                </div>
                <div className="d-flex flex-column align-items-start">
                    <Tooltip
                        title={`${email}`}
                        placement={Placement.TOP}
                        trigger="hover"
                        container="[data-video-comment-list]"
                    >
                        <div className="font-l5 font-l5--h7">{userName}</div>
                    </Tooltip>
                    <div className="font-l5 font-l5--subtitle-l color-grey-600">{commentTime}</div>
                </div>

                <div className={classNames(styles['video-comment__actions'], 'ms-l5-auto')}>
                    {/*
                        if threadType is not null, then this is first comment, show the actions for the thread
                        otherwise, just a normal comment in a thread, don't show any actions
                    */}
                    {!!threadType &&
                        !!window.SERVER.USER &&
                        (threadType === 'OPEN' ? (
                            <Tooltip
                                title="Resolve thread"
                                placement={Placement.TOP}
                                trigger="hover"
                                container="[data-video-comment-list]"
                            >
                                <a
                                    translate="no"
                                    className={classNames(
                                        'material-symbols-outlined',
                                        styles['resolve-comment'],
                                    )}
                                    onClick={handleResolveComment}
                                >
                                    check_circle
                                </a>
                            </Tooltip>
                        ) : (
                            <TertiaryButton
                                text="Re-open"
                                size={BUTTON_SIZES.SM}
                                variant={BUTTON_VARIANTS.OUTLINE}
                                className={classNames(styles['reopen-comment'], 'flex-shrink-0')}
                                onClick={handleReopenComment}
                            />
                        ))}
                    {isCommentByCurrentUser && (
                        <Tooltip
                            title="Delete comment"
                            placement={Placement.TOP}
                            trigger="hover"
                            container="[data-video-comment-list]"
                        >
                            <a
                                translate="no"
                                onClick={handleDelete}
                                className={classNames(
                                    'material-symbols-outlined btn-rounded',
                                    styles['delete-comment'],
                                )}
                            >
                                delete
                            </a>
                        </Tooltip>
                    )}
                </div>
            </div>
            <div className={styles['video-comment__content']}>
                <LexicalComposer
                    initialConfig={{
                        namespace: 'comment',
                        editable: false,
                        editorState,
                        onError: onLexicalError,
                        nodes: NODES,
                    }}
                >
                    <PlainTextPlugin
                        placeholder={<div />}
                        contentEditable={CONTENT_EDITABLE}
                        ErrorBoundary={LexicalErrorBoundary}
                    />
                </LexicalComposer>
            </div>
        </div>
    );
});

type VideoTranslateTextProps = {
    originalText: string;
    originalLanguage: string;
};

export const VideoTranslateText = React.memo(function VideoComment({
    originalText,
    originalLanguage,
}: VideoTranslateTextProps) {
    const splitText = originalText.split('\n');
    const text = splitText.map((textLine, index) => {
        return (
            <>
                <span>{textLine}</span>
                {index !== splitText.length - 1 ? <br /> : null}{' '}
                {/* add a line break if this isn't the last line of text */}
            </>
        );
    });
    return (
        <div className={styles['video-comment']}>
            <div className={styles['video-comment__header']}>
                <div className={classNames(styles['video-comment__avatar'], 'align-self-start')}>
                    <span
                        translate="no"
                        className={classNames(
                            styles['translate-icons'],
                            'material-symbols-outlined color-primary align-middle',
                        )}
                    >
                        g_translate
                    </span>
                </div>
                <div className="font-l5 font-l5--h7">
                    Translated{' '}
                    <span className={styles['video-comment__language']}>
                        from {originalLanguage}
                    </span>
                </div>
            </div>
            <div className={styles['video-comment__content']}>{text}</div>
        </div>
    );
});

/**
 * This component renders a list of <VideoComment />
 */

type VideoCommentsProps = {
    videoCommentIds?: readonly number[];
    threadType?: string;
    threadAction?: (data?: { comment_id: number }) => void;
    videoCreationSource: CreationSourceEnum | null;
    videoOriginalText?: string;
    parentVideoLanguageName?: string;
};

export const VideoComments = React.memo(function VideoComments({
    videoCommentIds = NO_COMMENT_IDS,
    threadType,
    threadAction,
    videoCreationSource,
    videoOriginalText,
    parentVideoLanguageName,
}: VideoCommentsProps) {
    return (
        <>
            {videoCreationSource === CreationSourceEnum.TRANSLATION && videoOriginalText && (
                <VideoTranslateText
                    originalText={videoOriginalText}
                    originalLanguage={parentVideoLanguageName ?? 'unknown'}
                />
            )}
            {videoCommentIds.map((videoCommentId, index) => {
                if (index === 0 && threadType === 'OPEN') {
                    return (
                        <VideoComment
                            threadType={threadType}
                            threadAction={threadAction}
                            key={videoCommentId}
                            commentId={videoCommentId}
                        />
                    );
                } else if (threadType === 'RESOLVED') {
                    return (
                        <VideoComment
                            threadType={threadType}
                            threadAction={threadAction}
                            key={videoCommentId}
                            commentId={videoCommentId}
                        />
                    );
                } else {
                    return <VideoComment key={videoCommentId} commentId={videoCommentId} />;
                }
            })}
        </>
    );
});

export default VideoComments;
